/**
 * De gesprekkenlijst links in de Easy-stand van Chats (02-10-2026, Jordy).
 *
 * Compacte rijen van twee regels, zoals de multi-chat van FanCentro:
 *   regel 1  modelfoto + model, fan, uitgegeven bedrag, wachttijd
 *   regel 2  laatste bericht, labels VIP / Private (met chatter) / Timewaster
 *
 * De rijen komen uit dezelfde wachtrij-query als de Pro-stand (zie
 * lib/panels.ts); deze lijst tekent alleen. Klikken opent het gesprek in de
 * middenkolom — nooit vanzelf, want openen telt als gelezen.
 */
import { InboxBadges } from './ChatPanels'
import { Avatar, Plat, Tag, Wacht } from './ui'
import { dtShort, eur } from '../lib/format'
import { urgentieVan } from '../lib/urgentie'
import type { QueueRow } from '../lib/panels'

/** Wat een rij uit de wachtrij-query verder nog draagt. */
export interface GesprekRij extends QueueRow {
  model_slug?: string | null
  stage_name?: string | null
  fan_id?: string | null
  fan_username?: string | null
  fan_display_name?: string | null
  last_message?: string | null
  is_vip?: boolean | null
  claimed_by_name?: string | null
  claimed_by_me?: boolean | null
  fan_custom_notes?: string | null
  last_message_at?: string | null
}

function fanNaam(c: GesprekRij): string {
  return String(c.fan_display_name || c.fan_username || c.fan_id || '—')
}

export function GesprekLijst({ rows, gekozen, onKies, avatarOf, leeg, beantwoord = [] }: {
  rows: GesprekRij[]
  /** Het gesprek dat nu in het midden open staat. */
  gekozen: number | null
  onKies: (c: GesprekRij) => void
  avatarOf?: (slug: unknown) => string | undefined
  /** Tekst als de lijst leeg is. */
  leeg: string
  /** 03-10-2026 (Jordy): bij een modelfilter ook de al beantwoorde chats van
   *  dat model, onder de wachtende, met hun laatste activiteit. */
  beantwoord?: GesprekRij[]
}) {
  if (!rows.length && !beantwoord.length) return <div className="easyleeg">{leeg}</div>
  const rij = (c: GesprekRij, isAnswered: boolean) => {
    const stamp = c.queue_fan_message_at || c.last_fan_message_at
    const laatst = c.last_message_at || c.last_response_at || c.last_fan_message_at
    const besteed = Number(c.fan_total_spend ?? 0)
    const model = String(c.stage_name || c.model_slug || '—')
    return (
      <button
        key={c.id}
        type="button"
        role="listitem"
        data-gesprek={c.id}
        className={`gesprekrij ${isAnswered ? 'answered' : `u-${urgentieVan(stamp)}`}${gekozen === c.id ? ' on' : ''}`}
        onClick={() => onKies(c)}
      >
        <span className="gr1">
          <Avatar name={model} src={avatarOf?.(c.model_slug)} size={20} />
          <span className="grmodel">{model}</span>
          <Plat value={c.platform} />
          <span className="grfan">{fanNaam(c)}</span>
          {c.fan_custom_notes ? (
            <span className="notitiestip" title={String(c.fan_custom_notes)}>⚠</span>
          ) : null}
          <span className="spacer" style={{ flex: 1 }} />
          {besteed > 0 ? <span className="grbedrag">{eur(besteed)}</span> : null}
          {isAnswered ? <span className="mut2 grtijd">{dtShort(laatst)}</span> : <Wacht sinds={stamp} />}
        </span>
        <span className="gr2">
          <span className="grtekst">{String(c.last_message ?? '')}</span>
          {/* 18-10-2026 (Jordy): zelfde inbox-badges als in Pro. */}
          <InboxBadges c={c} />
          {c.is_vip ? <Tag kind="acc">VIP</Tag> : null}
          {c.claimed_by_name ? (
            <Tag kind="warn" title={c.claimed_by_me ? 'Private to you' : undefined}>
              Private · {String(c.claimed_by_name)}
            </Tag>
          ) : null}
          {c.is_timewaster ? <Tag kind="bad">Timewaster</Tag> : null}
          {c.is_maybe_timewaster ? <Tag kind="warn">Maybe timewaster</Tag> : null}
        </span>
      </button>
    )
  }
  return (
    <div className="gesprekken" role="list">
      {rows.map((c) => rij(c, false))}
      {beantwoord.length ? (
        <>
          <div className="easykoptekst gesprektussen">Answered</div>
          {beantwoord.map((c) => rij(c, true))}
        </>
      ) : null}
    </div>
  )
}
